import React, {useState, useEffect} from "react"
import {BaseButton, BaseLoading, Icon} from "components"
import {BaseNewFilter, ConfirmPopup} from "uiKit"
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query"
import {calendarService} from "services"
import {flatten, isEmpty, map, uniq} from "lodash"
import {Button, Dropdown, Menu, Pagination, Tooltip} from "antd"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {copyTextToClipboard, handleError, toastSuccess} from "helpers"
import {Meeting} from "types/calendar"
import MeetingModal from "./MeetingModal"
import styles from "./Meetings.module.css"
import {useModel, useVisible} from "hooks"
import UserSelect from "components/UserSelect"
import {Auth} from "types/auth"

const Meetings = () => {
    const {t} = useTranslation(["calendar", "common"])
    const queryClient = useQueryClient()
    const meetingModal = useVisible(false)
    const confirmDeletePopup = useVisible(false)
    const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null)
    const [searchInput, setSearchInput] = useState("")
    const [page, setPage] = useState(1)
    const pageSize = 100
    const model = useModel()
    const [filterProfile, setFilterProfile] = useState(null)
    const campusIds = uniq(flatten(model.user.profiles.map((profile) => profile.campuses.map((campus) => campus.id))))

    useEffect(() => {
        setPage(1)
    }, [searchInput])

    const {data, isFetching} = useQuery({
        queryKey: ["meetings", searchInput, page, filterProfile],
        queryFn: () =>
            calendarService.getMeetings({
                filter: {search: searchInput, ownerUserId: filterProfile ? filterProfile.userId : undefined},
                pagination: {page, pageSize}
            }),
        initialData: {data: [], total: 0}
    })

    const meetings = data?.data || []
    const total = data?.total || 0

    const deleteMutation = useMutation((meetingIds: number[]) => calendarService.deleteMeeting(meetingIds), {
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["meetings"]})
            toastSuccess(t("common:message.deleteSuccess"))
            confirmDeletePopup.close()
        },
        onError: (err) => {
            handleError(err)
        }
    })

    const handleCreate = () => {
        setEditingMeeting(null)
        meetingModal.open()
    }

    const handleEdit = (meeting: Meeting) => {
        setEditingMeeting(meeting)
        meetingModal.open()
    }

    const handleDelete = (meeting: Meeting) => {
        setEditingMeeting(meeting)
        confirmDeletePopup.open()
    }

    const formatDuration = (minutes: number) => {
        if (minutes < 60) {
            return `${minutes} min`
        }
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`
    }

    const getRecurringInfo = (meeting: Meeting) => {
        if (!meeting.startAtUtc || !meeting.endAtUtc) {
            return "No end date"
        }
        const startDate = moment(meeting.startAtUtc)
        const endDate = moment(meeting.endAtUtc)
        return `${startDate.format("MMM D, YYYY")} - ${endDate.format("MMM D, YYYY")}`
    }

    const handleSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["meetings"]})
        meetingModal.close()
        setEditingMeeting(null)
    }

    const handleCopyLink = (meeting: Meeting) => {
        copyTextToClipboard(meeting.url)
        toastSuccess("Copied to clipboard")
    }

    const renderMenu = (meeting: Meeting) => (
        <Menu className={styles.menuActionWrap}>
            <Menu.Item key="edit" onClick={() => handleEdit(meeting)}>
                <div className={styles.menuActionItem}>
                    <Icon className={styles.menuActionIcon} icon="EDIT_LINE" />
                    <span className={styles.menuActionTitle}>Edit</span>
                </div>
            </Menu.Item>
            <Menu.Item key="delete" onClick={() => handleDelete(meeting)}>
                <div className={styles.menuActionItem}>
                    <Icon className={styles.menuActionIcon} icon="DELETE" />
                    <span className={styles.menuActionTitle}>Delete</span>
                </div>
            </Menu.Item>
        </Menu>
    )

    const renderContent = () => {
        if (isFetching) {
            return <BaseLoading isShow />
        }
        if (isEmpty(meetings)) {
            const message = !searchInput ? "Empty Meeting" : "No meetings found"
            return (
                <div className={styles.emptyState}>
                    <Icon icon="APPOINTMENT" className={styles.emptyIcon} />
                    <p className={styles.emptyText}>{message}</p>
                    {model.isStaffOrAdmin() && <BaseButton title="Create Meeting" onClick={handleCreate} />}
                </div>
            )
        }
        const isCurrentUser = !filterProfile || model.user.id === filterProfile.userId

        return (
            <div className={styles.meetingsList}>
                {map(meetings, (meeting) => (
                    <div key={meeting.meetingId} className={styles.meetingCard}>
                        <div className={styles.meetingContent}>
                            {meeting.jsonData.edularEventType?.color && (
                                <div
                                    className={styles.meetingColor}
                                    style={{backgroundColor: meeting.jsonData.edularEventType?.color}}
                                />
                            )}
                            <div className={styles.meetingHeader}>
                                <h3 className={styles.meetingName}>{meeting.name}</h3>
                                {isCurrentUser ? (
                                    <Tooltip title="Copy Link">
                                        <span className={styles.action} onClick={() => handleCopyLink(meeting)}>
                                            <Icon icon="COPY" />
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Open Link">
                                        <span
                                            className={styles.action}
                                            onClick={() => window.open(meeting.url, "_blank")}>
                                            <Icon icon="ARROW_RIGHT_CIRCLE" />
                                        </span>
                                    </Tooltip>
                                )}
                                {isCurrentUser && (
                                    <Dropdown overlay={renderMenu(meeting)} trigger={["click"]}>
                                        <Button
                                            className={styles.moreButton}
                                            icon={
                                                <Icon
                                                    className={styles.threeDotsIcon}
                                                    icon="MORE_VERTICAL"
                                                    color="#939393"
                                                />
                                            }
                                        />
                                    </Dropdown>
                                )}
                            </div>
                            <div className={styles.meetingDetails}>
                                <div className={styles.meetingDetailItem}>
                                    <Icon icon="TIME_CLOCK_OUTLINE" className={styles.detailIcon} />
                                    <span>{formatDuration(meeting.duration)}</span>
                                </div>
                                <div className={styles.meetingDetailItem}>
                                    <Icon icon="CALENDAR_FILL" className={styles.detailIcon} />
                                    <span>{getRecurringInfo(meeting)}</span>
                                </div>
                            </div>
                            {meeting.description && (
                                <div
                                    className={styles.meetingDescription}
                                    dangerouslySetInnerHTML={{
                                        __html: meeting.description
                                    }}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <BaseNewFilter
                onSearchInput={(value: string) => setSearchInput(value)}
                renderRightFilter={() => (
                    <div className={styles.filterWrapper}>
                        <UserSelect
                            type={Auth.UserProfileType.Staff}
                            value={filterProfile}
                            campusIds={campusIds}
                            onChange={(options) => setFilterProfile(options)}
                            isClearable
                        />
                        {model.isStaffOrAdmin() && (
                            <div className={styles.createButtonWrap}>
                                <BaseButton title="Create Meeting" onClick={handleCreate} />
                            </div>
                        )}
                    </div>
                )}
            />
            {renderContent()}
            {!isEmpty(meetings) && (
                <div className={styles.paginationWrapper}>
                    <Pagination
                        current={page}
                        total={total}
                        pageSize={pageSize}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        onChange={(newPage) => {
                            setPage(newPage)
                        }}
                    />
                </div>
            )}
            <MeetingModal
                isOpen={meetingModal.isVisible}
                onClose={meetingModal.close}
                meeting={editingMeeting}
                onSuccess={handleSuccess}
            />
            <ConfirmPopup
                title="Delete Meeting"
                isVisible={confirmDeletePopup.isVisible}
                onClose={confirmDeletePopup.close}
                onConfirm={() => deleteMutation.mutate([editingMeeting?.meetingId])}
                loading={deleteMutation.isLoading}
            />
        </div>
    )
}

export default Meetings
